import { Suspense, lazy, useMemo } from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';

import { useServiceFeatures } from 'hooks/useServiceContext';
import {
  LTL_UNIT_SYSTEMS,
  QuickQuoteInputs,
} from 'pages/QuoteView/Quoting/RequestQuickQuote/types';
import { IntegrationCore } from 'types/Integration';
import { Undef } from 'types/UtilityTypes';
import { TMS } from 'types/enums/Integrations';

import {
  resolveMaxStackHeightInches,
  resolveQuickQuoteShipmentFootprint,
} from '../utils/ltlMetrics';
import { resolveLTLStackabilityOptions } from '../utils/ltlStackability';
import { useCubicFeetHeightConfig } from './hooks/useCubicFeetHeightConfig';
import { useVisualizationLinearFeet } from './hooks/useVisualizationLinearFeet';

const TrailerVisualization = lazy(async () => {
  const module = await import('./components/TrailerVisualization');
  return { default: module.TrailerVisualization };
});

interface LTLTrailerVisualizationBlockProps {
  formMethods: UseFormReturn<QuickQuoteInputs, any, QuickQuoteInputs>;
}

function getActiveLTLVisualizationIntegrationName(
  ltlTMSIntegrations: Undef<IntegrationCore[]>
): Undef<TMS> {
  if (
    ltlTMSIntegrations?.some((integration) => integration.name === TMS.ThreeG)
  ) {
    return TMS.ThreeG;
  }

  if (
    ltlTMSIntegrations?.some((integration) => integration.name === TMS.Turvo)
  ) {
    return TMS.Turvo;
  }

  if (
    ltlTMSIntegrations?.some(
      (integration) => integration.name === TMS.McleodEnterprise
    )
  ) {
    return TMS.McleodEnterprise;
  }

  if (ltlTMSIntegrations?.some((integration) => integration.name === TMS.Tai)) {
    return TMS.Tai;
  }

  return undefined;
}

// Shared 3D trailer visualization for LTL quick quote. Render below the item
// list so layout matches across Tai / ThreeG / Turvo.
export function LTLTrailerVisualizationBlock({
  formMethods,
}: LTLTrailerVisualizationBlockProps) {
  const { control } = formMethods;

  const items = useWatch({ control, name: 'ltl.items' }) ?? [];

  const units =
    useWatch({ control, name: 'ltl.commodity.units' }) ??
    LTL_UNIT_SYSTEMS.IMPERIAL;

  const stackable =
    useWatch({ control, name: 'ltl.commodity.stackable' }) ?? false;

  const currentLinearFeet =
    useWatch({ control, name: 'ltl.commodity.linearFeet' }) ?? 0;

  const { ltlTMSIntegrations } = useServiceFeatures();
  const cubicFeetHeightConfig = useCubicFeetHeightConfig();
  const maxStackHeightInches = resolveMaxStackHeightInches(
    cubicFeetHeightConfig
  );

  const integrationName = useMemo(
    () => getActiveLTLVisualizationIntegrationName(ltlTMSIntegrations),
    [ltlTMSIntegrations]
  );

  const stackabilityOptions = useMemo(
    () =>
      resolveLTLStackabilityOptions({
        integrationName,
        stackingEnabled: stackable,
      }),
    [integrationName, stackable]
  );

  const quickQuoteFootprint = useMemo(
    () =>
      resolveQuickQuoteShipmentFootprint(items, units, {
        customConfig: cubicFeetHeightConfig,
        isItemStackable: stackabilityOptions.isItemStackable,
        maxStackHeightInches,
        stackingEnabled: stackabilityOptions.stackingEnabled,
      }),
    [
      cubicFeetHeightConfig,
      items,
      maxStackHeightInches,
      stackabilityOptions,
      units,
    ]
  );

  const visualizationLinearFeet = useVisualizationLinearFeet(
    currentLinearFeet,
    Math.ceil(quickQuoteFootprint.layout.totalLengthInches / 12)
  );

  return (
    <Suspense fallback={null}>
      <TrailerVisualization
        items={items}
        units={units}
        trailerLengthInches={Math.max(visualizationLinearFeet * 12 + 24, 24)}
        cubicFeetHeightConfig={cubicFeetHeightConfig}
        stackabilityOptions={stackabilityOptions}
        maxStackHeightInches={maxStackHeightInches}
        quickQuoteFootprint={quickQuoteFootprint}
      />
    </Suspense>
  );
}
